import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MsfPChartComponent } from '@components/msf-p-chart/msf-p-chart.component';
import { Loading } from '@components/ui/loading/loading';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import { PrimaryButton } from '@components/button/primary-button/primary-button';
import { StatusTag } from '@components/ui/status-tag/status-tag';
import { OrchestrationCatalogSearchComponent } from '@app/pages/admin/components/orchestration-catalog-search/orchestration-catalog-search';
import { OperationMonitoringService as OperationMonitoringApiService } from '@6grain/masofa-monolith-api-client/common/api/operationMonitoring.service';
import { InstanceFillSnapshot } from '@6grain/masofa-monolith-api-client/common/model/instanceFillSnapshot';
import { filterByCatalogQuery } from '@app/pages/admin/orchestration-catalog-search.util';
import {
  buildInstanceFillCountChart,
  buildInstanceFillStatusChart,
  instanceFillCountChartConfig,
  instanceFillStatusChartConfig,
  InstanceFillNavigateTarget,
  toInstanceFillCard,
  toInstanceFillTotals,
} from '@app/pages/admin/orchestration-instance-fill.util';

@Component({
  selector: 'app-orchestration-instance-fill-page',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    Loading,
    MsfPChartComponent,
    SecondaryButton,
    PrimaryButton,
    StatusTag,
    OrchestrationCatalogSearchComponent,
  ],
  templateUrl: './orchestration-instance-fill-page.html',
  styleUrl: './orchestration-instance-fill-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrchestrationInstanceFillPageComponent implements OnInit, OnDestroy {
  private readonly monitoring = inject(OperationMonitoringApiService);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly destroy$ = new Subject<void>();

  readonly loading = signal(true);
  readonly errorMessage = signal('');
  readonly snapshot = signal<InstanceFillSnapshot | null>(null);
  readonly searchQuery = signal('');
  readonly currentLang = signal(this.translate.currentLang);

  readonly totals = computed(() => toInstanceFillTotals(this.snapshot()?.steps ?? []));
  readonly countChartData = computed(() => {
    this.currentLang();
    return buildInstanceFillCountChart(
      this.snapshot()?.steps ?? [],
      key => this.translate.instant(key),
    );
  });
  readonly statusChartData = computed(() => {
    this.currentLang();
    return buildInstanceFillStatusChart(
      this.snapshot()?.steps ?? [],
      key => this.translate.instant(key),
    );
  });
  readonly countChartConfig = instanceFillCountChartConfig();
  readonly statusChartConfig = instanceFillStatusChartConfig();

  readonly visibleCards = computed(() => filterByCatalogQuery(
    (this.snapshot()?.steps ?? []).map(step => toInstanceFillCard(step)),
    this.searchQuery(),
    card => card.searchHaystack,
  ));

  ngOnInit(): void {
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(event => {
      this.currentLang.set(event.lang);
    });
    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  refresh(): void {
    this.load();
  }

  fill(target: InstanceFillNavigateTarget): void {
    void this.router.navigate(target.commands, { queryParams: target.queryParams });
  }

  private load(): void {
    this.loading.set(true);
    this.errorMessage.set('');
    this.monitoring.monitoringInstanceFillGet().pipe(takeUntil(this.destroy$)).subscribe({
      next: response => {
        this.snapshot.set(response);
        this.loading.set(false);
      },
      error: () => {
        this.errorMessage.set('Failed to load instance fill');
        this.loading.set(false);
      },
    });
  }
}
